/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { FORM_ADVANCED_SECTIONS } from '../schemas/formAdvancedSections';
import { FORM_COMPONENT_SCHEMAS } from '../schemas/formFieldSchema';
import { ComponentInputTypeT, FormAdvancedSectionsT } from '../schemas/types';
import { FormFieldT, FormItemT } from '../types';
import { isFieldSchema, isSectionSchema } from '../utils';

export const updateMissingTranslations = <T extends Record<string, any>>(
  obj: T,
  key: keyof T,
  defaultLanguage: string,
  languages: string[],
  translationValue?: string,
  defaultValue?: T,
): T => {
  if (!obj || !obj[key]) return obj; // Ensure the object and key exist

  const updatedObj = { ...obj, [key]: { ...obj[key] } };

  const defaultData = defaultValue?.[key]?.[defaultLanguage];
  const currentData = updatedObj?.[key]?.[defaultLanguage];

  languages.forEach((lang) => {
    if (lang !== defaultLanguage && !updatedObj[key][lang]?.trim()) {
      updatedObj[key][lang] = defaultData === currentData ? translationValue || currentData : currentData;
    }
  });

  return updatedObj;
};
export const translateSingleFieldSchema = (
  fieldValue: FormFieldT,
  newLang: string,
  defaultLang: string,
  activeLanguages: string[],
  baseDefaultValueFromType?: FormFieldT,
  specificDefaultOverride?: FormFieldT,
): Partial<FormFieldT> => {
  const effectiveDefault = specificDefaultOverride || baseDefaultValueFromType;
  if (!effectiveDefault) return {};
  const text = effectiveDefault?.text?.[newLang];
  const translatedText =
    effectiveDefault.text && text !== undefined && text.trim() !== ''
      ? { ...fieldValue.text, [newLang]: effectiveDefault?.text?.[newLang] }
      : updateMissingTranslations(fieldValue, 'text', defaultLang, activeLanguages, undefined, effectiveDefault)['text'];

  const label = effectiveDefault?.label?.[newLang];

  const translatedLabel =
    effectiveDefault.label && label !== undefined && label.trim() !== ''
      ? { ...fieldValue.label, [newLang]: effectiveDefault?.label?.[newLang] }
      : updateMissingTranslations(fieldValue, 'label', defaultLang, activeLanguages, undefined, effectiveDefault)['label'];

  const options = fieldValue?.optionList?.map((option) => {
    const defaultOptionSource = effectiveDefault?.optionList?.find((o) => o?.text?.[defaultLang] === option?.text?.[defaultLang]);
    const translatedOptionTextValue = defaultOptionSource?.text?.[newLang];
    return {
      ...option,
      key: translatedOptionTextValue || option.text?.[defaultLang] || option.id,
      text: updateMissingTranslations(
        option,
        'text',
        defaultLang,
        activeLanguages,
        translatedOptionTextValue,
        defaultOptionSource || option,
      )['text'],
    };
  });

  let documentReview = fieldValue?.documentReview;
  if (documentReview && effectiveDefault?.documentReview) {
    const defaultDocReview = effectiveDefault.documentReview;
    documentReview = {
      ...documentReview,
      description: updateMissingTranslations(
        documentReview,
        'description',
        defaultLang,
        activeLanguages,
        defaultDocReview.description?.[newLang],
        defaultDocReview,
      )['description'],
      acknowledgeContent: updateMissingTranslations(
        documentReview,
        'acknowledgeContent',
        defaultLang,
        activeLanguages,
        defaultDocReview.acknowledgeContent?.[newLang],
        defaultDocReview,
      )['acknowledgeContent'],
      confirmText: updateMissingTranslations(
        documentReview,
        'confirmText',
        defaultLang,
        activeLanguages,
        defaultDocReview.confirmText?.[newLang],
        defaultDocReview,
      )['confirmText'],
      url: { ...documentReview.url, [newLang]: defaultDocReview.url?.[newLang] ?? '' },
    };
  }

  return {
    text: translatedText,
    label: translatedLabel,
    optionList: options,
    documentReview,
  };
};

export const translateFullFormSchema = (
  items: FormItemT[],
  newLang: string,
  defaultLang: string,
  activeLanguages: string[],
  questionAdvancedMap: Record<string, ComponentInputTypeT>,
): FormItemT[] => {
  return items.map((item) => {
    if (isSectionSchema(item)) {
      const translatedFields = translateFullFormSchema(item.fields, newLang, defaultLang, activeLanguages, questionAdvancedMap);
      return { ...item, fields: translatedFields };
    }
    if (isFieldSchema(item)) {
      let baseDefaultValue = FORM_COMPONENT_SCHEMAS[item.inputType as ComponentInputTypeT]?.['defaultValue'] as
        | FormFieldT
        | undefined;
      if (
        item.section &&
        Object.values(FORM_ADVANCED_SECTIONS).includes(item.section as FORM_ADVANCED_SECTIONS) &&
        questionAdvancedMap[item.section as FormAdvancedSectionsT]
      ) {
        const advancedSectionType = questionAdvancedMap[item.section as FormAdvancedSectionsT];
        const advancedDefaults = FORM_COMPONENT_SCHEMAS[advancedSectionType]?.['defaultValue'] as FormFieldT[] | undefined;
        const foundAdvancedDefault = advancedDefaults?.find((df) => df.text?.[defaultLang] === item.text?.[defaultLang]);
        if (foundAdvancedDefault) {
          baseDefaultValue = foundAdvancedDefault;
        }
      }
      const translatedProperties = translateSingleFieldSchema(item, newLang, defaultLang, activeLanguages, baseDefaultValue);
      let translatedFollowUps;
      if (item.followUpQuestionList && Array.isArray(item.followUpQuestionList)) {
        translatedFollowUps = item.followUpQuestionList.map((followUp) => {
          if (isFieldSchema(followUp)) {
            const defaultFollowUpSource = baseDefaultValue?.followUpQuestionList?.find(
              (f) => isFieldSchema(f) && f.text?.[defaultLang] === followUp.text?.[defaultLang],
            ) as FormFieldT | undefined;
            const translatedFollowUpProps = translateSingleFieldSchema(
              followUp,
              newLang,
              defaultLang,
              activeLanguages,
              FORM_COMPONENT_SCHEMAS[followUp.inputType as ComponentInputTypeT]?.['defaultValue'] as FormFieldT | undefined,
              defaultFollowUpSource,
            );
            return { ...followUp, ...translatedFollowUpProps };
          }
          if (isSectionSchema(followUp)) {
            const translatedFields = translateFullFormSchema(
              followUp.fields,
              newLang,
              defaultLang,
              activeLanguages,
              questionAdvancedMap,
            );
            return { ...followUp, fields: translatedFields };
          }
          return followUp;
        });
      }
      return {
        ...item,
        ...translatedProperties,
        followUpQuestionList: translatedFollowUps,
      };
    }
    console.warn('translateFullFormSchema encountered an item that is neither FieldSchema nor SectionSchema:', item);
    return item;
  });
};
